import React, { useEffect, useState } from 'react';
import { isAfter } from 'date-fns';
import { graphql, useLazyLoadQuery } from 'react-relay';
import {
	type AnalyticsEventPayload,
	type UIAnalyticsEvent,
	useAnalyticsEvents,
} from '@atlaskit/analytics-next';

import type { ProjectType } from '@atlassian/jira-common-constants';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type {
	main_UpgradeOnboarding_EntitlementInfo_Query,
	main_UpgradeOnboarding_EntitlementInfo_Query$data,
} from '@atlassian/jira-relay/src/__generated__/main_UpgradeOnboarding_EntitlementInfo_Query.graphql';
import {
	STANDARD_EDITION,
	getApplicationForProject,
	getEdition,
} from '@atlassian/jira-shared-types';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';

import { API_SUCCESS, FETCH_PAYMENT_DETAILS_API_CALL, SEND_OPERATIONAL_EVENT } from './constants';
import { isUpgradedBasedOnTraits } from './services';
import { useHasBeenUpgradedFromFree, useHasMultipleCCPProducts } from './services/main';
import { getModalViewedProperty } from './services/user-properties';
import { ExperimentContainer } from './ui';
import { projectToProductKey, projectTypeToApplicationKey } from './utils';

export interface ExperimentComponentProps {
	projectType: ProjectType;
}

export const Query = graphql`
	query main_UpgradeOnboarding_EntitlementInfo_Query($cloudId: ID!, $hamsProductKey: String!) {
		tenantContexts(cloudIds: [$cloudId]) {
			entitlementInfo(hamsProductKey: $hamsProductKey) {
				entitlement {
					... on CcpEntitlement {
						transactionAccountId
					}
					transactionAccount {
						experienceCapabilities {
							addPaymentMethod {
								isAvailableToUser
								...main_upgradeOnboardingExperiment_UpgradeOnboardingModal
							}
						}
					}
					subscription {
						accountDetails {
							invoiceGroup {
								experienceCapabilities {
									configurePayment {
										isAvailableToUser
									}
								}
								invoiceable
							}
						}
					}
				}
			}
		}
	}
`;

const sendSuccessAnalytics = (
	createAnalyticsEvent: (payload: AnalyticsEventPayload) => UIAnalyticsEvent,
	data: main_UpgradeOnboarding_EntitlementInfo_Query$data,
) => {
	const analyticsSetSuccessEvent = createAnalyticsEvent({
		type: SEND_OPERATIONAL_EVENT,
		action: API_SUCCESS,
		actionSubject: FETCH_PAYMENT_DETAILS_API_CALL,
	});
	fireOperationalAnalytics(
		analyticsSetSuccessEvent,
		`${FETCH_PAYMENT_DETAILS_API_CALL} ${API_SUCCESS}`,
		{
			data,
		},
	);
};

export const UpgradeOnboardingExperimentComponent = ({ projectType }: ExperimentComponentProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId, atlassianAccountId, appEditions, productEntitlementDetails } =
		useTenantContext();
	const editionFromTenantContext = getEdition(getApplicationForProject(projectType), appEditions);
	const product = projectTypeToApplicationKey(projectType);

	const [canProceed, setCanProceed] = useState(false);

	const hasBeenUpgradedFromFree = useHasBeenUpgradedFromFree({
		currentEdition: editionFromTenantContext,
		product,
	});

	useEffect(() => {
		(async () => {
			if (editionFromTenantContext === STANDARD_EDITION) {
				const entitlementDetails = productEntitlementDetails?.[product];

				if (!atlassianAccountId || !entitlementDetails) {
					return;
				}

				const billingSourceSystem = entitlementDetails.billingSourceSystem;
				const trialEndTime = entitlementDetails.trialEndTime;
				const isOnTrial = trialEndTime && isAfter(trialEndTime, Date.now());

				if (isOnTrial && entitlementDetails.entitlementId) {
					const wasModalViewed = await getModalViewedProperty(
						createAnalyticsEvent,
						projectType,
						atlassianAccountId,
					);

					if (!wasModalViewed) {
						if (billingSourceSystem === 'CCP') {
							setCanProceed(true);
							return;
						}

						const hasUpgraded = await isUpgradedBasedOnTraits(product, cloudId, undefined);

						setCanProceed(hasUpgraded);
					}
				}
			}
		})();
	}, [
		atlassianAccountId,
		cloudId,
		createAnalyticsEvent,
		editionFromTenantContext,
		product,
		productEntitlementDetails,
		productEntitlementDetails?.entitlementId,
		projectType,
	]);

	const isEnabled = hasBeenUpgradedFromFree || canProceed;

	return isEnabled ? <UpgradeOnboardingExperiment projectType={projectType} /> : <></>;
};

export const UpgradeOnboardingExperiment = ({ projectType }: ExperimentComponentProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useTenantContext();

	const data = useLazyLoadQuery<main_UpgradeOnboarding_EntitlementInfo_Query>(Query, {
		cloudId,
		hamsProductKey: projectToProductKey(projectType),
	});

	useEffect(() => {
		data && sendSuccessAnalytics(createAnalyticsEvent, data);
	}, [createAnalyticsEvent, data]);

	const entitlement =
		data?.tenantContexts && data.tenantContexts.length > 0
			? data.tenantContexts[0]?.entitlementInfo?.entitlement
			: undefined;
	const invoiceGroup = entitlement?.subscription?.accountDetails?.invoiceGroup;

	const addPaymentMethod =
		entitlement?.transactionAccount?.experienceCapabilities?.addPaymentMethod;
	// transactionAccountId can only exist if the billing system is CCP
	const transactionAccountId = entitlement?.transactionAccountId;
	const configurePaymentCapability = invoiceGroup?.experienceCapabilities?.configurePayment;
	const invoiceable = invoiceGroup?.invoiceable;

	const canPay = Boolean(
		configurePaymentCapability?.isAvailableToUser || addPaymentMethod?.isAvailableToUser,
	);

	const { isLoading: isLoadingEntitlements, hasMultiple } = useHasMultipleCCPProducts(
		projectType,
		canPay && !invoiceable,
		transactionAccountId,
	);

	// check experiment enrollment conditions
	const isEnabled =
		// only enroll if user can actually add payment details
		canPay &&
		// only enroll if site doesn't have payment details
		!invoiceable &&
		// we can only tell if a site has been upgraded once per product per transactionAccountId
		// so we exclude sites with multiples of the same product associated to their transaction account
		!isLoadingEntitlements &&
		!hasMultiple;

	return isEnabled ? (
		<ExperimentContainer
			addPaymentMethodRef={addPaymentMethod}
			projectType={projectType}
			transactionAccountId={transactionAccountId}
		/>
	) : (
		<></>
	);
};
